// AntD ConfigProvider theme derived from the CID logo palette. Dark mode swaps in the
// darkAlgorithm + dark surfaces so AntD overlays (message, modal, drawer) follow the app.
import { theme as antdTheme } from 'antd';

const POPPINS = "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const DARK = { bg: '#0E1420', surface: '#18202E', border: '#2A3648', text: '#E7EDF6', sub: '#93A2B8' };

// One field height for the whole app — must match --field-h in styles/design.css.
// Pinned onto every input-like component below (default AND large AND small) so a
// stray size="large" can't make one row taller than the one under it. Buttons keep
// AntD's own scale: they're actions, not fields, and several footers set their own.
const FIELD_H = 46;
const FIELD = { controlHeight: FIELD_H, controlHeightLG: FIELD_H, controlHeightSM: FIELD_H };

export function makeTheme(isDark) {
  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: isDark ? '#4A93DE' : '#18609C',
      colorInfo: isDark ? '#4A93DE' : '#18609C',
      colorLink: isDark ? '#4A93DE' : '#18609C',
      colorSuccess: '#2F9E6F',
      colorWarning: '#D98A1E',
      colorError: '#D05050',
      colorBgLayout: isDark ? DARK.bg : '#EEF2F7',
      borderRadius: 12,
      controlHeight: 42,
      fontFamily: POPPINS,
      fontSize: 14,
      // AntD portals its popups to <body> at a base of 1000 (Modal 1000, message
      // 1010, Popover 1030). This app's own full-screen overlays sit far above
      // that — sheets at 4500/4600, the update prompt at 5000 — so every confirm
      // dialog and toast raised from inside one was rendering behind it. A
      // confirm you can't see is a confirm you can't accept, which silently
      // broke destructive actions. Lift the whole popup layer clear of the app's
      // own ceiling; keep sheet z-indexes below 6000 so this stays true.
      zIndexPopupBase: 6000,
      ...(isDark
        ? {
            colorBgContainer: DARK.surface,
            colorBgElevated: DARK.surface,
            colorBorder: DARK.border,
            colorBorderSecondary: DARK.border,
            colorText: DARK.text,
            colorTextSecondary: DARK.sub,
          }
        : {
            // --border from design.css. AntD's stock #d9d9d9 is a shade darker
            // than every hand-rolled field in the app (SearchBar, SheetSelect),
            // so bordering them all off the same token is what makes a form read
            // as one control set rather than two.
            colorBorder: '#E3E8F0',
            colorBorderSecondary: '#E3E8F0',
          }),
    },
    components: {
      Button: { primaryShadow: 'none', fontWeight: 600 },
      Input: FIELD,
      InputNumber: FIELD,
      Select: FIELD,
      DatePicker: FIELD,
      // A Segmented sits directly between hand-rolled cards on several screens
      // (the Earnings deal list, the sale/listing/letter forms), and those cards
      // are 14px. AntD would give the track the global 12 and the thumb
      // borderRadiusSM (6), so the same screen carried two families of corner.
      // 14 outside, 12 inside — the track padding is 2px, which keeps the two
      // curves concentric instead of the thumb looking pinched.
      Segmented: { ...FIELD, borderRadius: 14, borderRadiusSM: 12 },
    },
  };
}
